import { Link as RouterLink } from 'react-router-dom';
import { resolveBuildRunNumber } from '@/features/builds/utils/buildRunNumber';
import { toPipelineSlug } from '@/features/pipelines/utils/pipelineSlug';
import { formatEntityTagsLabel } from '@/features/shared/tags';
import { formatFileSizeKb } from '@/features/uploads/formatters/uploadFormatters';
import { useResolvedFeatureFlags } from '@/shared/hooks';
import { s } from '@/theme/tokens';
import { InfoField } from '@/ui/composites/InfoField';
import { formatDate } from '@/ui/formatters/formatDate';
import { LayoutGrid, LayoutGridItem } from '@/ui/layout/LayoutGrid';
import { Section } from '@/ui/layout/Section';
import { Stack } from '@/ui/layout/Stack';
import {
  Box,
  Chip,
  formatStatusLabel,
  Link,
  resolveStatusChipTone,
  Surface,
} from '@/ui/primitives';
import type { BuildDto, ResultsFileDto } from '@flowqa/shared';
import type { ReactElement } from 'react';

export type ResultsFileDetailsPanelsProps = {
  resultsFile: ResultsFileDto;
  build: BuildDto | null;
  layout?: 'rows' | 'columns';
};

export const ResultsFileDetailsPanels = ({
  resultsFile,
  build,
  layout = 'rows',
}: ResultsFileDetailsPanelsProps): ReactElement => {
  const noDataLabel = 'No data';
  const noDataText = (
    <Box component="span" sx={{ color: 'text.secondary' }}>
      {noDataLabel}
    </Box>
  );
  const { flags } = useResolvedFeatureFlags();
  const operationsEnabled = flags?.operations ?? true;
  const hasBuildAssociation = Boolean(build?.id ?? resultsFile.buildId);
  const showBuildPanel = operationsEnabled && hasBuildAssociation;
  const tagsLabel = formatEntityTagsLabel(resultsFile.tags);
  const buildStatusLabel = build?.status ? formatStatusLabel(build.status) : noDataLabel;
  const pipelineLabel = build?.pipelineName ?? build?.pipelineId ?? noDataLabel;
  const pipelineSlug = build?.pipelineName ? toPipelineSlug(build.pipelineName) : null;
  const pipelineSegment =
    pipelineSlug && pipelineSlug.length > 0 ? pipelineSlug : (build?.pipelineId ?? null);
  const buildRunNumber = resolveBuildRunNumber(build?.buildKey);
  const pipelineHref = pipelineSegment ? `/pipelines/${pipelineSegment}` : '/pipelines';
  const buildHref =
    pipelineSegment && buildRunNumber !== null
      ? `/pipelines/${pipelineSegment}/runs/${buildRunNumber}`
      : pipelineHref;
  const buildLabel = build?.buildKey ?? build?.id ?? resultsFile.buildId ?? noDataLabel;

  return (
    <Section paddingY="md">
      <Surface padding="lg" border>
        {layout === 'columns' ? (
          <LayoutGrid gap="md">
            <LayoutGridItem xs={12} sm={showBuildPanel ? 4 : 6}>
              <Stack
                gap="sm"
                sx={{ paddingRight: s('md'), borderRight: 0.5, borderColor: 'divider' }}
              >
                <InfoField label="File Name" value={resultsFile.fileName} gap="none" />
                <InfoField label="Size" value={formatFileSizeKb(resultsFile.size)} gap="none" />
                <InfoField
                  label="Project"
                  value={
                    resultsFile.projectName ?? resultsFile.projectSlug ?? resultsFile.projectId
                  }
                  gap="none"
                />
                <InfoField label="Tags" value={tagsLabel} gap="none" />
              </Stack>
            </LayoutGridItem>
            <LayoutGridItem xs={12} sm={showBuildPanel ? 4 : 6}>
              <Stack
                gap="sm"
                sx={[
                  { paddingX: s('md') },
                  ...(showBuildPanel ? [{ borderRight: 0.5, borderColor: 'divider' }] : []),
                ]}
              >
                <InfoField
                  label="Uploaded"
                  value={formatDate(resultsFile.createdAt, { includeTime: true }) || noDataLabel}
                  gap="none"
                />
                <InfoField label="Uploaded By" value={resultsFile.createdByName} gap="none" />
                <InfoField
                  label="Upload ID"
                  value={
                    resultsFile.uploadId ? (
                      <Link
                        component={RouterLink}
                        to={`/uploads/${resultsFile.uploadId}`}
                        underline="hover"
                        color="inherit"
                      >
                        {resultsFile.uploadId}
                      </Link>
                    ) : (
                      noDataText
                    )
                  }
                  gap="none"
                />
              </Stack>
            </LayoutGridItem>
            {showBuildPanel ? (
              <LayoutGridItem xs={12} sm={4}>
                <Stack gap="sm" sx={{ paddingLeft: s('md') }}>
                  <InfoField
                    label="Build"
                    value={
                      build?.id ? (
                        <Link
                          component={RouterLink}
                          to={buildHref}
                          underline="hover"
                          color="inherit"
                        >
                          {buildLabel}
                        </Link>
                      ) : (
                        buildLabel
                      )
                    }
                    gap="none"
                  />
                  <InfoField
                    label="Pipeline"
                    value={
                      pipelineSegment ? (
                        <Link
                          component={RouterLink}
                          to={pipelineHref}
                          underline="hover"
                          color="inherit"
                        >
                          {pipelineLabel}
                        </Link>
                      ) : (
                        pipelineLabel
                      )
                    }
                    gap="none"
                  />
                  <InfoField
                    label="Build Status"
                    value={
                      build?.status ? (
                        <Chip
                          label={buildStatusLabel}
                          tone={resolveStatusChipTone(build.status)}
                          size="xs"
                          transform="uppercase"
                        />
                      ) : (
                        noDataText
                      )
                    }
                    gap="none"
                  />
                </Stack>
              </LayoutGridItem>
            ) : null}
          </LayoutGrid>
        ) : (
          <Stack gap="md">
            <LayoutGrid gap="md">
              <LayoutGridItem xs={12} sm={6}>
                <Stack gap="sm">
                  <InfoField label="File Name" value={resultsFile.fileName} gap="none" />
                  <InfoField label="Size" value={formatFileSizeKb(resultsFile.size)} gap="none" />
                  <InfoField
                    label="Project"
                    value={
                      resultsFile.projectName ?? resultsFile.projectSlug ?? resultsFile.projectId
                    }
                    gap="none"
                  />
                  <InfoField label="Tags" value={tagsLabel} gap="none" />
                </Stack>
              </LayoutGridItem>
              <LayoutGridItem xs={12} sm={6}>
                <Stack gap="sm">
                  <InfoField
                    label="Uploaded"
                    value={formatDate(resultsFile.createdAt, { includeTime: true }) || noDataLabel}
                    gap="none"
                  />
                  <InfoField label="Uploaded By" value={resultsFile.createdByName} gap="none" />
                  <InfoField
                    label="Upload ID"
                    value={
                      resultsFile.uploadId ? (
                        <Link
                          component={RouterLink}
                          to={`/uploads/${resultsFile.uploadId}`}
                          underline="hover"
                          color="inherit"
                        >
                          {resultsFile.uploadId}
                        </Link>
                      ) : (
                        noDataText
                      )
                    }
                    gap="none"
                  />
                </Stack>
              </LayoutGridItem>
            </LayoutGrid>
            {showBuildPanel ? (
              <LayoutGrid gap="md">
                <LayoutGridItem xs={12} sm={4}>
                  <InfoField
                    label="Build"
                    value={
                      build?.id ? (
                        <Link
                          component={RouterLink}
                          to={buildHref}
                          underline="hover"
                          color="inherit"
                        >
                          {buildLabel}
                        </Link>
                      ) : (
                        buildLabel
                      )
                    }
                    gap="none"
                  />
                </LayoutGridItem>
                <LayoutGridItem xs={12} sm={4}>
                  <InfoField
                    label="Pipeline"
                    value={
                      pipelineSegment ? (
                        <Link
                          component={RouterLink}
                          to={pipelineHref}
                          underline="hover"
                          color="inherit"
                        >
                          {pipelineLabel}
                        </Link>
                      ) : (
                        pipelineLabel
                      )
                    }
                    gap="none"
                  />
                </LayoutGridItem>
                <LayoutGridItem xs={12} sm={4}>
                  <InfoField
                    label="Build Status"
                    value={
                      build?.status ? (
                        <Chip
                          label={buildStatusLabel}
                          tone={resolveStatusChipTone(build.status)}
                          size="xs"
                          transform="uppercase"
                        />
                      ) : (
                        noDataText
                      )
                    }
                    gap="none"
                  />
                </LayoutGridItem>
              </LayoutGrid>
            ) : null}
          </Stack>
        )}
      </Surface>
    </Section>
  );
};
